import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { Warning } from '@mui/icons-material';
import { SUMMARY_STATUS_PAGE_TEXT, PROGRESS_NOTIFICATION_TEXT } from './lang';
import { OnlineRegistration } from './Registration/OnlineRegistration';
import { ApplicationStatusSideBar } from './ApplicationStatusSideBar';
import { APPLICATION_STATUS_MODULE } from './constants';
import { FEE_TEXT } from './Payment/lang';
import { RefereeStatusTable } from './RefereeForm';
import { ApplicationFeePayment } from './Payment';
import { SupervisorInvitation } from './SupervisorInvitation';
import {
  NoticeOfOffer,
  ConditionMeet,
  QualificationCheckStatus,
  VisaApplication,
} from './Offer';
import { PAYMENT_TYPE, type APPLICATION_MODULE, ReturnRemarks } from '..';
import { type ApplicationToDoList, ApplicationFeeStatus } from '../../graphql';
import { type ApplicationRemarkType } from '../../../Nomination/graphql';
import { NotificationText, Loading, Alert } from '../../../../components';
import { APPLICATION_STATUS_TYPE } from '../../../../utils';
import { useNotification } from '../../graphql/ApplicationStatus/Registration/useGetStudentNotification';

const applicationStepBanner = (
  <div className="text-[#951D1D] rounded bg-[#E6BEBE75] flex flex-row items-center w-full px-6 py-4">
    <Warning
      sx={{
        marginRight: '2rem',
        fontSize: '3rem',
      }}
    />
    <div
      dangerouslySetInnerHTML={{
        __html: SUMMARY_STATUS_PAGE_TEXT.APPLICATION_FEE_BANNER,
      }}
    />
  </div>
);

export type ApplicationStatusContentProps = {
  applicationNo: string;
  defaultSidebarActivated: APPLICATION_STATUS_MODULE;
  applicationToDoList: ApplicationToDoList;
  returnRemarks: ApplicationRemarkType[];
  entryYear: string;
  handleChangeApplicationActivated: (
    activatedModule: APPLICATION_MODULE,
  ) => void;
  refetch: () => void;
  refetchApplication: () => void;
};

type ComponentModuleType = {
  component: any;
  moduleTitle: string;
  banner?: React.ReactNode;
};

export const ApplicationStatusContent: React.FC<
  ApplicationStatusContentProps
> = (props: ApplicationStatusContentProps) => {
  const {
    defaultSidebarActivated,
    applicationToDoList,
    returnRemarks,
    applicationNo,
    entryYear,

    handleChangeApplicationActivated,
    refetch,
    refetchApplication,
  } = props;

  const { todoList, visibleList, applicationStatus, isLocal, studentNumber } =
    applicationToDoList;

  // display notification text beyond the top bar
  const { notification, loading, error } = useNotification(entryYear);
  const offerAcceptedNote = notification;

  const progressNotificationMapping: Partial<
    Record<APPLICATION_STATUS_TYPE, string>
  > = {
    [APPLICATION_STATUS_TYPE.LAPSED]: PROGRESS_NOTIFICATION_TEXT.OFFER_LAPSED,
    [APPLICATION_STATUS_TYPE.CONDITION_MEET_LAPSED]:
      PROGRESS_NOTIFICATION_TEXT.CONDITION_MET_LAPSED,
    [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_PENDING]: offerAcceptedNote,
    [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_SETTLED]: offerAcceptedNote,
    [APPLICATION_STATUS_TYPE.UNDER_REGISTRATION]: offerAcceptedNote,
    [APPLICATION_STATUS_TYPE.REGISTERED]: offerAcceptedNote,
  };

  const progressNotification =
    progressNotificationMapping[applicationStatus as APPLICATION_STATUS_TYPE] ??
    '';

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [activatedId, setActivatedId] = useState<APPLICATION_STATUS_MODULE>(
    defaultSidebarActivated,
  );

  useEffect(() => {
    searchParams.set('aphb', activatedId);
    // Update the URL with new Module
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [activatedId]);

  const nonDisplayModuleTitleList = [
    APPLICATION_STATUS_MODULE.REGISTRATION,
    APPLICATION_STATUS_MODULE.CONFIRM_OFFER,
  ];

  const isDisplayModuleTitle = !nonDisplayModuleTitleList.includes(activatedId);
  const noteUnderDebitNote = FEE_TEXT.noteUnderDebitNote;
  const debitNoteStatusTextMap: Record<ApplicationFeeStatus, string> = {
    [ApplicationFeeStatus.PAID]: FEE_TEXT.applicationFeeHelperText,
    [ApplicationFeeStatus.NOT_PAID]: FEE_TEXT.applicationFeeHelperText,
    [ApplicationFeeStatus.NOT_CREATED]: FEE_TEXT.applicationFeeHelperText,
    [ApplicationFeeStatus.PAST_DUE]: FEE_TEXT.applicationFeeHelperText,
    [ApplicationFeeStatus.CANCELLED]: FEE_TEXT.applicationFeeHelperText,
    [ApplicationFeeStatus.WAIVED]: FEE_TEXT.applicationFeeWaivedText,
    [ApplicationFeeStatus.SUSPENDED]: FEE_TEXT.debitNoteReady,
    // TODO: pending for Rosie to add the helper text for this status
    [ApplicationFeeStatus.NOT_AVAILABLE]: '',
  };
  const componentMap: Partial<
    Record<APPLICATION_STATUS_MODULE, ComponentModuleType>
  > = {
    [APPLICATION_STATUS_MODULE.APPLICATION_FEE_PAYMENT]: {
      component: (
        <ApplicationFeePayment
          paymentType={PAYMENT_TYPE.APPLICATION_FEE}
          helperText={debitNoteStatusTextMap}
        />
      ),
      moduleTitle: SUMMARY_STATUS_PAGE_TEXT.PAYMENT_STATUS,
      banner: applicationStepBanner,
    },
    [APPLICATION_STATUS_MODULE.ACADEMIC_REFEREE_STATUS]: {
      component: <RefereeStatusTable />,
      moduleTitle: SUMMARY_STATUS_PAGE_TEXT.ACADEMIC_REFEREE_STATUS,
      banner: applicationStepBanner,
    },
    [APPLICATION_STATUS_MODULE.SUPERVISOR_INVITATION]: {
      component: <SupervisorInvitation />,
      moduleTitle: SUMMARY_STATUS_PAGE_TEXT.SUPERVISOR_INVITATION,
      banner: applicationStepBanner,
    },
    [APPLICATION_STATUS_MODULE.RETURN_REMARKS]: {
      component: <ReturnRemarks returnRemarks={returnRemarks} />,
      moduleTitle: SUMMARY_STATUS_PAGE_TEXT.RETURN_REMARKS,
    },
    [APPLICATION_STATUS_MODULE.CONFIRM_OFFER]: {
      component: (
        <NoticeOfOffer
          applicationNo={applicationNo}
          refetchTodoList={refetch}
        />
      ),
      moduleTitle: SUMMARY_STATUS_PAGE_TEXT.OFFER_ACCEPTANCE,
    },

    [APPLICATION_STATUS_MODULE.DEBIT_NOTE]: {
      component: (
        <ApplicationFeePayment
          paymentType={PAYMENT_TYPE.REGISTRATION_FEE}
          helperText={FEE_TEXT.registrationFeeHelperText}
          underNote={noteUnderDebitNote}
        />
      ),
      moduleTitle: SUMMARY_STATUS_PAGE_TEXT.DEBIT_NOTE,
    },
    [APPLICATION_STATUS_MODULE.VISA_APPLICATION]: {
      component: <VisaApplication />,
      moduleTitle: SUMMARY_STATUS_PAGE_TEXT.VISA_APPLICATION,
    },

    [APPLICATION_STATUS_MODULE.CONDITIONAL_OFFER_CONFIRMATION]: {
      component: (
        <ConditionMeet
          refetchApplication={refetchApplication}
          handleChangeApplicationActivated={handleChangeApplicationActivated}
        />
      ),
      moduleTitle: SUMMARY_STATUS_PAGE_TEXT.CONDITIONAL_OFFER_CONFIRMATION,
    },

    [APPLICATION_STATUS_MODULE.QUALIFICATION_CHECK]: {
      component: <QualificationCheckStatus entryYear={entryYear} />,
      moduleTitle: SUMMARY_STATUS_PAGE_TEXT.QUALIFICATION_CHECK,
    },
    [APPLICATION_STATUS_MODULE.TUITION_FEE_PAYMENT]: {
      component: (
        <ApplicationFeePayment
          paymentType={PAYMENT_TYPE.TUITION_FEE}
          helperText={FEE_TEXT.tuitionFeeHelperText}
          underNote={FEE_TEXT.tuitionFeeUnderText}
        />
      ),
      moduleTitle: SUMMARY_STATUS_PAGE_TEXT.TUITION_FEE_PAYMENT,
    },

    [APPLICATION_STATUS_MODULE.REGISTRATION]: {
      component: (
        <OnlineRegistration
          isLocal={isLocal}
          studentNumber={studentNumber}
          entryYear={entryYear}
          refetchApplicationNum={refetch}
        />
      ),
      moduleTitle: SUMMARY_STATUS_PAGE_TEXT.ONLINE_REGISTRATION,
    },
  };

  const activatedModule: ComponentModuleType | undefined =
    componentMap[activatedId];

  const moduleComponent = activatedModule?.component ?? activatedId;
  const moduleTitle = activatedModule?.moduleTitle ?? activatedId;
  const moduleBanner = activatedModule?.banner ?? null;

  const handleChangeActivated = (
    activatedModule: APPLICATION_STATUS_MODULE,
  ): void => {
    setActivatedId(activatedModule);
  };

  if (error as boolean) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <div>
      {loading ? (
        <Loading />
      ) : (
        <ApplicationStatusContainer>
          <Container>
            <div className="top-bar-area">
              {progressNotification.length > 0 && (
                <div className="w-full pl-2.5 pr-2.5">
                  <NotificationText note={progressNotification} />
                </div>
              )}
              <ApplicationStatusSideBar
                applicationStatus={applicationStatus as APPLICATION_STATUS_TYPE}
                todoList={todoList}
                visibleList={visibleList}
                defaultActivatedId={defaultSidebarActivated}
                onChangeActivated={handleChangeActivated}
              />
            </div>

            {Boolean(moduleBanner) && (
              <div className="px-3">{moduleBanner}</div>
            )}
            {isDisplayModuleTitle && <h1>{moduleTitle}</h1>}
            <div className="form-area px-4">{moduleComponent}</div>
          </Container>
        </ApplicationStatusContainer>
      )}
    </div>
  );
};

const ApplicationStatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  width: 100%;
  position: relative;
  min-width: 900px;
  overflow: auto;
  @media print {
    .top-bar-area {
      display: none !important;
    }
    font-size: 10px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 100vh;

  .form-area {
    width: 100%;
  }

  h1 {
    margin-top: 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    text-align: center;
  }

  .top-bar-area {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  .banner-link {
    color: #006daa;
    text-decoration: underline;
  }
`;
